import { useState } from "react";
import { Disclosure } from "./disclosure";
import { FormGroup } from "./form-group";
import { IconInfo } from "./icons";
import { Input, TextArea } from "./input";
import { Popover } from "./popover";
import { Select } from "./select";

export const LabelWithInfo = ({
  label,
  info,
  docsUrl,
}: {
  label: string;
  info: string | React.ReactNode;
  docsUrl?: string;
}) => (
  <span className="flex items-center gap-1">
    {label}
    <Popover trigger={<IconInfo className="w-4 h-4 text-black-300" />}>
      <span>
        {info}
        {docsUrl ? (
          <>
            {" "}
            <a
              href={docsUrl}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Learn more
            </a>
          </>
        ) : null}
      </span>
    </Popover>
  </span>
);

// ---- Security fields ----

const IpAllowlistField = ({
  value,
  onChange,
  error,
}: { value: string; onChange: (v: string) => void; error?: string }) => (
  <FormGroup
    label="IP Allowlist"
    htmlFor="ip-allowlist"
    description="Only traffic from the following sources is allowed. Add more sources (IPv4 addresses and CIDRs) by separating them with spaces or newlines."
    feedbackMessage={error}
    feedbackVariant={error ? "danger" : "info"}
  >
    <TextArea
      id="ip-allowlist"
      aria-label="ip-allowlist"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  </FormGroup>
);

const ForceSslField = ({
  value,
  onChange,
}: { value: string; onChange: (v: string) => void }) => (
  <FormGroup
    label={
      <LabelWithInfo
        label="Force SSL"
        info="Redirect all HTTP requests to HTTPS, and enable the Strict-Transport-Security header (HSTS)."
        docsUrl="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/https-redirect"
      />
    }
    htmlFor="force-ssl"
  >
    <Select
      ariaLabel="Force SSL"
      id="force-ssl"
      value={value}
      options={[
        { label: "Accept platform default (currently off)", value: "" },
        { label: "On", value: "true" },
        { label: "Off", value: "false" },
      ]}
      onSelect={(opt) => onChange(opt.value)}
    />
  </FormGroup>
);

const SslProtocolsOverrideField = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
}) => (
  <FormGroup
    label={
      <LabelWithInfo
        label="SSL Protocols Override"
        info="Specify a list of allowed SSL protocols."
        docsUrl="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/https-protocols"
      />
    }
    htmlFor="ssl-protocols-override"
  >
    <Select
      ariaLabel="SSL Protocols Override"
      id="ssl-protocols-override"
      value={value}
      options={options}
      onSelect={(opt) => onChange(opt.value)}
    />
  </FormGroup>
);

const SslCiphersOverrideField = ({
  value,
  onChange,
}: { value: string; onChange: (v: string) => void }) => (
  <FormGroup
    label={
      <LabelWithInfo
        label="SSL Ciphers Override"
        info="Specify the allowed SSL ciphers."
        docsUrl="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/https-protocols"
      />
    }
    htmlFor="ssl-ciphers-override"
  >
    <Input
      id="ssl-ciphers-override"
      name="ssl-ciphers-override"
      type="text"
      placeholder="Accept platform default"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  </FormGroup>
);

const DisableWeakCipherSuitesField = ({
  value,
  onChange,
}: { value: string; onChange: (v: string) => void }) => (
  <FormGroup
    label={
      <LabelWithInfo
        label="Disable Weak Cipher Suites"
        info="Prevents acceptance of SSLv3 and RC4 cipher traffic. Applies to legacy ELB endpoints only (weak ciphers are already disabled on ALB)."
        docsUrl="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/https-protocols"
      />
    }
    htmlFor="disable-weak-cipher-suites"
  >
    <Select
      ariaLabel="Disable Weak Cipher Suites"
      id="disable-weak-cipher-suites"
      value={value}
      options={[
        { label: "Accept platform default", value: "" },
        { label: "Enabled", value: "true" },
        { label: "Disabled", value: "false" },
      ]}
      onSelect={(opt) => onChange(opt.value)}
    />
  </FormGroup>
);

// ---- Healthchecks fields ----

const StrictHealthChecksField = ({
  value,
  onChange,
}: { value: string; onChange: (v: string) => void }) => (
  <FormGroup
    label={
      <LabelWithInfo
        label="Strict Health Checks"
        info="Require containers to respond to health checks with a 200 OK HTTP response."
        docsUrl="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/health-checks"
      />
    }
    htmlFor="strict-health-checks"
  >
    <Select
      ariaLabel="Strict Health Checks"
      id="strict-health-checks"
      value={value}
      options={[
        { label: "Accept platform default (currently off)", value: "" },
        { label: "On", value: "true" },
        { label: "Off", value: "false" },
      ]}
      onSelect={(opt) => onChange(opt.value)}
    />
  </FormGroup>
);

const ReleaseHealthcheckTimeoutField = ({
  value,
  onChange,
  error,
}: { value: string; onChange: (v: string) => void; error?: string }) => (
  <FormGroup
    label={
      <LabelWithInfo
        label="Release Healthcheck Timeout"
        info="Timeout (seconds) to wait for your app to respond to a release health check."
        docsUrl="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/health-checks"
      />
    }
    htmlFor="release-healthcheck-timeout"
    feedbackMessage={error}
    feedbackVariant={error ? "danger" : "info"}
  >
    <Input
      id="release-healthcheck-timeout"
      name="release-healthcheck-timeout"
      type="number"
      placeholder="Accept platform default (currently 180s)"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      min={1}
      max={900}
    />
  </FormGroup>
);

const IdleTimeoutField = ({
  value,
  onChange,
  error,
}: { value: string; onChange: (v: string) => void; error?: string }) => (
  <FormGroup
    label={
      <LabelWithInfo
        label="Idle Timeout"
        info="Timeout (seconds) to enforce idle timeouts while sending and receiving responses."
        docsUrl="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/overview"
      />
    }
    htmlFor="idle-timeout"
    feedbackMessage={error}
    feedbackVariant={error ? "danger" : "info"}
  >
    <Input
      id="idle-timeout"
      name="idle-timeout"
      type="number"
      placeholder="Accept platform default (currently 60s)"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      min={30}
      max={2400}
    />
  </FormGroup>
);

const ShowElbHealthchecksField = ({
  value,
  onChange,
}: { value: string; onChange: (v: string) => void }) => (
  <FormGroup
    label={
      <LabelWithInfo
        label="Show ELB Health Checks in Logs"
        info="Show all runtime health check requests in the endpoint's logs."
        docsUrl="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/endpoint-logs"
      />
    }
    htmlFor="show-elb-healthchecks"
  >
    <Select
      ariaLabel="Show ELB Health Checks"
      id="show-elb-healthchecks"
      value={value}
      options={[
        { label: "Accept platform default (currently off)", value: "" },
        { label: "On", value: "true" },
        { label: "Off", value: "false" },
      ]}
      onSelect={(opt) => onChange(opt.value)}
    />
  </FormGroup>
);

const MaintenancePageUrlField = ({
  value,
  onChange,
  error,
}: { value: string; onChange: (v: string) => void; error?: string }) => (
  <FormGroup
    label={
      <LabelWithInfo
        label="Maintenance Page URL"
        info="The URL of a maintenance page to cache and serve when requests time out, or your app is unhealthy."
        docsUrl="https://www.aptible.com/docs/core-concepts/apps/connecting-to-apps/app-endpoints/https-endpoints/maintenance-page"
      />
    }
    htmlFor="maintenance-page-url"
    feedbackMessage={error}
    feedbackVariant={error ? "danger" : "info"}
  >
    <Input
      id="maintenance-page-url"
      name="maintenance-page-url"
      type="text"
      placeholder="An unbranded maintenance page will be served"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  </FormGroup>
);

// ---- Section components ----

interface EndpointSecuritySettingsProps {
  applies: (setting: string) => boolean;
  sslProtocolsOptions: { label: string; value: string }[];
  ipAllowlist: string;
  onIpAllowlistChange: (v: string) => void;
  ipAllowlistError?: string;
  forceSSL: string;
  onForceSSLChange: (v: string) => void;
  sslProtocolsOverride: string;
  onSslProtocolsOverrideChange: (v: string) => void;
  sslCiphersOverride: string;
  onSslCiphersOverrideChange: (v: string) => void;
  disableWeakCipherSuites: string;
  onDisableWeakCipherSuitesChange: (v: string) => void;
  tokenFormSlot?: React.ReactNode;
}

export const EndpointSecuritySettings = ({
  applies,
  sslProtocolsOptions,
  ipAllowlist,
  onIpAllowlistChange,
  ipAllowlistError,
  forceSSL,
  onForceSSLChange,
  sslProtocolsOverride,
  onSslProtocolsOverrideChange,
  sslCiphersOverride,
  onSslCiphersOverrideChange,
  disableWeakCipherSuites,
  onDisableWeakCipherSuitesChange,
  tokenFormSlot,
}: EndpointSecuritySettingsProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="py-4">
      <Disclosure
        labelWhenClosed="Security"
        labelWhenOpen="Security"
        open={open}
        onToggle={() => setOpen((prev) => !prev)}
        labelClassName="text-md"
      >
        <div className="mt-4 ml-6 flex flex-col gap-4">
          <IpAllowlistField
            value={ipAllowlist}
            onChange={onIpAllowlistChange}
            error={ipAllowlistError}
          />
          {applies("FORCE_SSL") ? (
            <ForceSslField value={forceSSL} onChange={onForceSSLChange} />
          ) : null}
          {applies("SSL_PROTOCOLS_OVERRIDE") ? (
            <SslProtocolsOverrideField
              value={sslProtocolsOverride}
              onChange={onSslProtocolsOverrideChange}
              options={sslProtocolsOptions}
            />
          ) : null}
          {applies("SSL_CIPHERS_OVERRIDE") ? (
            <SslCiphersOverrideField
              value={sslCiphersOverride}
              onChange={onSslCiphersOverrideChange}
            />
          ) : null}
          {applies("DISABLE_WEAK_CIPHER_SUITES") ? (
            <DisableWeakCipherSuitesField
              value={disableWeakCipherSuites}
              onChange={onDisableWeakCipherSuitesChange}
            />
          ) : null}
          {tokenFormSlot}
        </div>
      </Disclosure>
    </div>
  );
};

interface EndpointHealthchecksSettingsProps {
  applies: (setting: string) => boolean;
  strictHealthChecks: string;
  onStrictHealthChecksChange: (v: string) => void;
  releaseHealthcheckTimeout: string;
  onReleaseHealthcheckTimeoutChange: (v: string) => void;
  releaseHealthcheckTimeoutError?: string;
  idleTimeout: string;
  onIdleTimeoutChange: (v: string) => void;
  idleTimeoutError?: string;
  showElbHealthchecks: string;
  onShowElbHealthchecksChange: (v: string) => void;
  maintenancePageUrl: string;
  onMaintenancePageUrlChange: (v: string) => void;
  maintenancePageUrlError?: string;
  lbAlgorithmSlot?: React.ReactNode;
}

export const EndpointHealthchecksSettings = ({
  applies,
  strictHealthChecks,
  onStrictHealthChecksChange,
  releaseHealthcheckTimeout,
  onReleaseHealthcheckTimeoutChange,
  releaseHealthcheckTimeoutError,
  idleTimeout,
  onIdleTimeoutChange,
  idleTimeoutError,
  showElbHealthchecks,
  onShowElbHealthchecksChange,
  maintenancePageUrl,
  onMaintenancePageUrlChange,
  maintenancePageUrlError,
  lbAlgorithmSlot,
}: EndpointHealthchecksSettingsProps) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="py-4">
      <Disclosure
        labelWhenClosed="Healthchecks and Request handling"
        labelWhenOpen="Healthchecks and Request handling"
        open={open}
        onToggle={() => setOpen((prev) => !prev)}
        labelClassName="text-md"
      >
        <div className="mt-4 ml-6 flex flex-col gap-4">
          {lbAlgorithmSlot}
          {applies("STRICT_HEALTH_CHECKS") ? (
            <StrictHealthChecksField
              value={strictHealthChecks}
              onChange={onStrictHealthChecksChange}
            />
          ) : null}
          {applies("RELEASE_HEALTHCHECK_TIMEOUT") ? (
            <ReleaseHealthcheckTimeoutField
              value={releaseHealthcheckTimeout}
              onChange={onReleaseHealthcheckTimeoutChange}
              error={releaseHealthcheckTimeoutError}
            />
          ) : null}
          {applies("IDLE_TIMEOUT") ? (
            <IdleTimeoutField
              value={idleTimeout}
              onChange={onIdleTimeoutChange}
              error={idleTimeoutError}
            />
          ) : null}
          {applies("SHOW_ELB_HEALTHCHECKS") ? (
            <ShowElbHealthchecksField
              value={showElbHealthchecks}
              onChange={onShowElbHealthchecksChange}
            />
          ) : null}
          {applies("MAINTENANCE_PAGE_URL") ? (
            <MaintenancePageUrlField
              value={maintenancePageUrl}
              onChange={onMaintenancePageUrlChange}
              error={maintenancePageUrlError}
            />
          ) : null}
        </div>
      </Disclosure>
    </div>
  );
};
